import * as React from 'react';
import { Avatar, Box, Divider, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { SettingsDialog } from '../settings/SettingsDialog';
import { signIn, signOut } from '../../app/auth/appAuth';
import { useIdentity } from '../../app/auth/sessionIdentity';
import { NavIcon } from './navIcons';
import { RAIL } from './railTokens';

const { account: ACCOUNT } = RAIL;
const MENU_GAP = ACCOUNT.menuGap;
const MENU_GAP_BOTTOM = ACCOUNT.menuBottom;
// Phone: the menu drops 4px below the 56px header, 12px in from the right edge.
const PHONE_MENU_TOP = 60;

/**
 * The rail's one account control: the avatar block at the foot of the panel, the menu it opens
 * (Profile, Settings, Sign in or Sign out) and the Settings dialog the menu hosts.
 *
 * It replaces the header's three controls — the "whoami" caption, the settings gear and the avatar.
 * Settings is reachable once, from the menu.
 *
 * The menu sits 8px right of the rail edge with its bottom 12px above the viewport bottom: `left` is
 * 256 while the rail is open and 80 while it is closed (handoff steps 2 and 5), the same in project
 * mode, where the closed panel's edge is also at 72 before its 62px tuck. `menuOpen` is owned by the
 * rail and forces the panel open, so in practice the menu opens at 256. The position is computed from
 * `open`, not measured from the panel.
 *
 * ⚠️ IT IS NOT ANCHORED TO THE PANEL ELEMENT. That was the first build, and a real browser measured two
 * defects: (1) a quick click opens the menu while the .24s width transition is still running, the
 * Popover measures the anchor mid-transition and lands at 72+8 over the user block, and it never
 * re-measures; (2) MUI clamps the paper to `marginThreshold` (16) from the viewport edge, then the
 * paper's own `mt: -12px` lifted it further, leaving it ~28px from the bottom instead of 12.
 */
export function RailAccount(props: {
  /** `phone`: only the 36px avatar, in the phone header, with the menu dropping from the top right. */
  variant?: 'rail' | 'phone';
  open: boolean;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  onClearProjects?: () => void;
}) {
  const { open, menuOpen, onMenuOpenChange } = props;
  const { t } = useTranslation();
  const identity = useIdentity();
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  // The Popover reads `anchorPosition` at render, so the viewport height is state while the menu is open:
  // a taller window would otherwise leave the menu floating where the old bottom edge was.
  const [viewportHeight, setViewportHeight] = React.useState(() => window.innerHeight);
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const sync = () => setViewportHeight(window.innerHeight);
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [menuOpen]);

  // N-3 follow-on (2026-09-06): the un-named fallback initial is derived from the SAME localized
  // placeholder the name line renders, not a hardcoded 'U' — a zh-CN caller with no bound display
  // name must not read 用户 beside an avatar that says U.
  const name = identity.firstName ?? t('nav:header.userPlaceholder');
  const avatarInitial = name.charAt(0).toUpperCase();
  // Persist-then-render: the role line comes from the verified session (GET /api/me). Nothing is
  // drawn for it while identity is loading, so it never flips from "Not signed in" to a role.
  const roleLine = identity.loading
    ? null
    : identity.authenticated
      ? (identity.isAdmin ? t('nav:header.administrator') : t('nav:header.signedIn'))
      : t('nav:header.notSignedIn');

  const close = () => onMenuOpenChange(false);
  const phone = props.variant === 'phone';
  const [viewportWidth, setViewportWidth] = React.useState(() => window.innerWidth);
  React.useEffect(() => {
    if (!menuOpen || !phone) return undefined;
    const sync = () => setViewportWidth(window.innerWidth);
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [menuOpen, phone]);
  // The menu is a card, so it follows the theme: the handoff's white card and #404040 ink are light-only.
  const dark = useTheme().palette.mode === 'dark';
  const itemSx = {
    py: '10px',
    px: 2,
    gap: '12px',
    fontSize: 14,
    fontWeight: 400,
    color: dark ? '#e5e5e5' : '#404040',
    '&:hover': { background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
  } as const;

  return (
    <>
      <Box sx={{ flex: 'none', p: phone ? 0 : '12px' }}>
        <Box
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-controls={menuOpen ? 'account-menu' : undefined}
          aria-label={t('nav:header.tooltipAccount')}
          data-testid="rail-user"
          onClick={() => onMenuOpenChange(!menuOpen)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onMenuOpenChange(!menuOpen);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            p: phone ? 0 : '6px',
            borderRadius: phone ? '50%' : '12px',
            cursor: 'pointer',
            background: open ? ACCOUNT.openFill : 'transparent',
            transition: 'background 0.2s',
            '&:hover': { background: phone ? 'transparent' : ACCOUNT.hoverFill },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              flex: 'none',
              bgcolor: 'var(--hg)',
              color: 'var(--hn)',
              fontSize: 14,
              fontWeight: 700,
              boxShadow: ACCOUNT.avatarShadow,
            }}
          >
            {avatarInitial}
          </Avatar>
          {!phone && <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pr: 1,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              opacity: open ? 1 : 0,
              maxWidth: open ? 200 : 0,
              transition: RAIL.fadeTransition,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                data-testid="rail-user-name"
                sx={{ fontSize: 14, fontWeight: 600, color: '#fff', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {name}
              </Typography>
              {roleLine && (
                <Typography
                  data-testid="rail-user-role"
                  sx={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.3px', color: ACCOUNT.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {roleLine}
                </Typography>
              )}
            </Box>
            <NavIcon name="chevronsUpDown" size={16} strokeWidth={2} style={{ color: ACCOUNT.muted }} />
          </Box>}
        </Box>
      </Box>

      <Menu
        id="account-menu"
        open={menuOpen}
        onClose={close}
        anchorReference="anchorPosition"
        anchorPosition={phone
          ? { left: viewportWidth - MENU_GAP_BOTTOM, top: PHONE_MENU_TOP }
          : { left: (open ? RAIL.openWidth : RAIL.collapsedWidth) + MENU_GAP, top: viewportHeight - MENU_GAP_BOTTOM }}
        transformOrigin={phone ? { horizontal: 'right', vertical: 'top' } : { horizontal: 'left', vertical: 'bottom' }}
        marginThreshold={MENU_GAP_BOTTOM}
        MenuListProps={{ sx: { p: '0 0 8px' } }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              boxShadow: 'none',
              borderRadius: '16px',
              minWidth: 200,
              background: dark ? '#2d2d2d' : 'linear-gradient(135deg,#fff,#fafbfd)',
              border: dark ? '1px solid #404040' : '1px solid #ededed',
              filter: `drop-shadow(0px 8px 24px rgba(0,0,0,${dark ? 0.5 : 0.18}))`,
            },
          },
        }}
      >
        <MenuItem sx={itemSx} onClick={close}>
          <ListItemIcon sx={{ minWidth: 'auto', color: 'inherit' }}><NavIcon name="user" size={20} /></ListItemIcon>
          {t('nav:header.profile')}
        </MenuItem>
        <MenuItem sx={itemSx} onClick={() => { close(); setSettingsOpen(true); }}>
          <ListItemIcon sx={{ minWidth: 'auto', color: 'inherit' }}><NavIcon name="settings" size={20} /></ListItemIcon>
          {t('nav:header.settings')}
        </MenuItem>
        {/* Sign in ↔ Sign out — rendered only once identity resolves, so the menu never flashes the
            wrong action. Anonymous → Sign in (→ /.auth/login/aad). Signed in → Sign out (clears both
            cookie planes + the SWA session). */}
        {!identity.loading && <Divider sx={{ my: '4px', borderColor: dark ? '#404040' : '#ededed' }} />}
        {!identity.loading && (
          identity.authenticated ? (
            <MenuItem sx={{ ...itemSx, color: dark ? '#ff9a95' : '#bd2327' }} onClick={() => { close(); void signOut(); }}>
              <ListItemIcon sx={{ minWidth: 'auto', color: 'inherit' }}><NavIcon name="signOut" size={20} /></ListItemIcon>
              {t('nav:header.signOut')}
            </MenuItem>
          ) : (
            <MenuItem sx={itemSx} onClick={() => { close(); signIn(); }}>
              <ListItemIcon sx={{ minWidth: 'auto', color: 'inherit' }}><NavIcon name="signOut" size={20} style={{ transform: 'scaleX(-1)' }} /></ListItemIcon>
              {t('nav:header.signIn')}
            </MenuItem>
          )
        )}
      </Menu>

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onClearProjects={props.onClearProjects}
      />
    </>
  );
}
